import { useState, useEffect } from 'react'
import { ArrowLeft, Coins, Loader2, MoveUpRight, QrCode } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBalance } from '@/hooks/useBalance'
import { useTransactions } from '@/hooks/useTransactions'
import { useCurrency } from '@/hooks/useCurrency'
import { useWalletStore } from '@/store/walletStore'
import { getNetwork } from '@/config/networks'
import TradingChart from '@/components/TradingChart'
import AssetIcon from '@/components/AssetIcon'
import AssetStats from '@/components/AssetStats'
import Skeleton from '@/components/common/Skeleton'
import LatestTransaction from '@/components/LatestTransaction'
import TransactionDetailDialog from '@/components/dialogs/TransactionDetailDialog'
import { Transaction } from '@/types/transaction.types'

const AssetDetails = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { code } = useParams<{ code: string }>()
  const { tokens, allNetworksTokens, loading } = useBalance()
  const { formatValue } = useCurrency()
  const selectedNetwork = useWalletStore((state) => state.selectedNetwork)

  const token =
    tokens.find((tk) => tk.symbol === code) ||
    allNetworksTokens.find((tk) => tk.symbol === code)

  const { transactions, loading: txLoading } = useTransactions(token?.network)
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)

  const assetTransactions = token
    ? transactions.filter((tx) =>
        token.isNative
          ? !tx.tokenAddress
          : tx.tokenAddress?.toLowerCase() === token.address.toLowerCase()
      )
    : []

  const formatAddress = (addr: string) => {
    if (!addr) return ''
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`
  }

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return t('time.just_now')
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return t('time.m_ago', { count: minutes })
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return t('time.h_ago', { count: hours })
    return new Date(timestamp).toLocaleDateString()
  }

  const [imgState, setImgState] = useState<'primary' | 'fallback' | 'error'>(
    'primary'
  )

  useEffect(() => {
    setImgState('primary')
  }, [token?.logoUrl])

  const getEthereumFallback = (url: string): string | null => {
    const match = url.match(/blockchains\/[^/]+\/assets\/([^/]+)\/logo\.png/)
    if (!match) return null
    const fallback = `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${match[1]}/logo.png`
    return fallback !== url ? fallback : null
  }

  const handleImgError = () => {
    if (imgState === 'primary' && token?.logoUrl) {
      const fallback = getEthereumFallback(token.logoUrl)
      if (fallback) {
        setImgState('fallback')
        return
      }
    }
    setImgState('error')
  }

  const activeSrc =
    imgState === 'fallback' && token?.logoUrl
      ? getEthereumFallback(token.logoUrl)
      : token?.logoUrl

  const isSendDisabled = !!token && selectedNetwork !== token.network

  const handleSend = () => {
    if (!token || isSendDisabled) return
    navigate('/send', {
      state: { presetSymbol: token.symbol, locked: true }
    })
  }

  if (loading || !token) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-text-main" />
          </button>
        </div>
        {loading ? (
          <div className="flex flex-col gap-4">
            <p className="flex items-center justify-center gap-2 text-sm text-text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('assetDetails.switching_network', {
                network: getNetwork(selectedNetwork)?.name
              })}
            </p>
            <Skeleton className="h-16 w-full rounded-2xl" />
            <Skeleton className="h-88 w-full rounded-3xl" />
            <Skeleton className="h-32 w-full rounded-3xl" />
          </div>
        ) : (
          <p className="text-text-secondary text-center">
            {t('assets.no_assets')}
          </p>
        )}
      </div>
    )
  }

  const change = token.priceChange24h || 0
  const isPositive = change >= 0
  const priceUSD = token.priceUSD || 0
  const balanceAmount = parseFloat(token.balanceFormatted || '0')
  const balanceUSD = balanceAmount * priceUSD

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-x-12 w-full max-w-lg lg:max-w-5xl mx-auto pt-4">
      <div className="flex items-center gap-4 lg:col-start-1 lg:row-start-1">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-text-main" />
        </button>
      </div>

      <div className="flex items-center justify-between lg:col-start-1 lg:row-start-2">
        <div className="flex items-center gap-4">
          <AssetIcon size="w-12 h-12">
            {activeSrc && imgState !== 'error' ? (
              <img
                key={activeSrc}
                src={activeSrc}
                alt={token.name}
                onError={handleImgError}
                className="w-full h-full rounded-full object-cover p-0.5"
              />
            ) : (
              <Coins className="icon-gradient w-6 h-6" />
            )}
          </AssetIcon>
          <h1 className="font-bold text-2xl text-text-main">{token.symbol}</h1>
        </div>

        <div className="flex flex-col items-end">
          <p className="font-bold text-2xl text-text-main">
            {formatValue(priceUSD)}
          </p>
          <p
            className={`text-sm font-semibold ${isPositive ? 'text-success' : 'text-accent'}`}
          >
            {isPositive ? '+' : ''}
            {change.toFixed(2)}%
          </p>
        </div>
      </div>

      <div className="lg:col-start-2 lg:mt-8 lg:row-start-1 lg:row-span-5">
        <TradingChart symbol={token.symbol} />
      </div>

      <div className="flex flex-col gap-1 bg-card border border-border-base rounded-3xl p-6 shadow-md lg:col-start-1 lg:row-start-3">
        <p className="text-xs text-text-secondary uppercase tracking-widest font-bold">
          {t('assetDetails.your_balance')}
        </p>
        <p className="font-bold text-2xl text-text-main">
          {formatValue(balanceUSD)}
        </p>
        <p className="text-sm text-text-secondary">
          {token.balanceFormatted || '0.00'} {token.symbol}
        </p>
      </div>

      <div className="flex flex-col gap-2 lg:col-start-1 lg:row-start-4">
        <div className="flex gap-4">
          <button
            onClick={handleSend}
            disabled={isSendDisabled}
            title={
              isSendDisabled ? t('assetDetails.send_disabled_wrong_network') : undefined
            }
            className="btn-gradient-fill flex w-1/2 justify-center py-4 rounded-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
          >
            {t('dashboard.send')}
            <MoveUpRight className="w-4 h-4 ml-2" />
          </button>
          <button
            onClick={() => navigate('/receive')}
            className="btn-gradient-outline w-1/2 flex justify-center py-4 rounded-2xl font-bold"
          >
            {t('dashboard.receive')}
            <QrCode className="w-4 h-4 ml-2 flex justify-center dark:text-primary text-accent" />
          </button>
        </div>
        {isSendDisabled && (
          <p className="text-xs text-text-secondary text-center">
            {t('assetDetails.send_disabled_wrong_network')}
          </p>
        )}
      </div>

      <AssetStats
        symbol={token.symbol}
        network={token.network}
        address={token.address}
        priceChange24h={change}
        className="lg:col-start-1 lg:col-span-2 lg:row-start-6"
      />

      <div className="flex flex-col gap-6 lg:col-start-1 lg:col-span-2 lg:row-start-7">
        <h2 className="text-text-main font-bold text-2xl">
          {t('dashboard.latest_transactions')}
        </h2>
        <div className="flex flex-col gap-4">
          {txLoading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)
          ) : assetTransactions.length > 0 ? (
            assetTransactions.map((tx) => (
              <LatestTransaction
                key={tx.hash}
                title={
                  tx.type === 'send'
                    ? t('dashboard.sent')
                    : t('dashboard.received')
                }
                type={tx.type}
                address={formatAddress(tx.type === 'send' ? tx.to : tx.from)}
                fullAddress={tx.type === 'send' ? tx.to : tx.from}
                amount={
                  (tx.type === 'send' ? '-' : '+') +
                  Number(tx.value || '0').toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 8
                  })
                }
                coin={tx.tokenSymbol || token.symbol}
                time={getTimeAgo(tx.timestamp)}
                onClick={() => setSelectedTx(tx)}
              />
            ))
          ) : (
            <div className="py-10 text-center border-2 border-dashed border-text-main/10 rounded-3xl">
              <p className="text-text-secondary">
                {t('assetDetails.no_transactions')}
              </p>
            </div>
          )}
        </div>
      </div>

      <TransactionDetailDialog
        open={!!selectedTx}
        tx={selectedTx}
        onClose={() => setSelectedTx(null)}
      />
    </div>
  )
}

export default AssetDetails
